import { memo, useCallback, useState } from 'react'

import { AutoDeleteOutlined } from '@mui/icons-material'
import { ListItemIcon, Typography, Divider, MenuItem } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { ActionMenu, AnimatedButton } from '@shared/components/ContextMenu'
import { Dialog } from '@shared/components/MaterialUIEnhancements/Dialog'
import { MenuItemCopyId, MenuItemDelete } from '@shared/components/MaterialUIEnhancements/Menu/MenuItem'
import { LifecyclePolicyDialog } from 'domains/Workspaces/Video/Objects'
import { useVideoObjectDeleteMutation } from 'hooks/mutations/content'
import { videoObjectsOverviewRoute } from 'pages/NWPlatform/Workspaces/Workspace/Video/Objects'
import { videoObjectRoute } from 'pages/NWPlatform/Workspaces/Workspace/Video/Objects/Object'
import { videoObjectQueryKeys } from 'services/queryKeys'

const ActionsMenuRaw = () => {
  const { t } = useTranslation(['common', 'domain', 'entity'])
  const { enqueueSnackbar } = useSnackbar()

  const { videoobjectId } = videoObjectRoute.useParams()
  const navigate = useNavigate({ from: videoObjectRoute.fullPath })

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement>()
  const [dialog, setDialog] = useState(false)

  const videoObjectQuery = useQuery(videoObjectQueryKeys.detail({ videoId: videoobjectId }))
  const { data: videoObject } = videoObjectQuery

  const { mutate: deleteVideoObject } = useVideoObjectDeleteMutation()

  const handleDeleteVideoObject = useCallback(() => {
    if (!videoObject) return

    setAnchorEl(undefined)

    deleteVideoObject(
      { videoId: videoObject.id },
      {
        onSuccess: () => {
          enqueueSnackbar(t('domain:Workspace.Video.nameDelete', { name: videoObject.name }), { variant: 'success' })

          setTimeout(() => {
            navigate({ to: videoObjectsOverviewRoute.to, replace: true })
          }, 300)
        },
        onError: () => {
          enqueueSnackbar(t('somethingWentWrong'), { variant: 'error' })
        },
      },
    )
  }, [videoObject, deleteVideoObject, enqueueSnackbar, navigate, t])

  return (
    <>
      <AnimatedButton
        open={!!anchorEl}
        IconButtonProps={{
          onClick: (e) => setAnchorEl(e.currentTarget),
          color: 'inherit',
        }}
      />
      <ActionMenu
        anchorEl={anchorEl}
        onClose={() => {
          setAnchorEl(undefined)
        }}
        title={videoObject?.name}
        entity={t('entity:content.videoObject', { count: 1 })}
      >
        <MenuItem
          onClick={() => {
            setAnchorEl(undefined)

            setTimeout(() => {
              setDialog(true)
            }, 300)
          }}
        >
          <ListItemIcon>
            <AutoDeleteOutlined />
          </ListItemIcon>
          <Typography sx={{ width: '100%' }}>{t('domain:Workspace.Video.setLifecyclePolicy', { count: 1 })}</Typography>
        </MenuItem>

        <MenuItemCopyId onSuccess={() => setAnchorEl(undefined)} id={videoobjectId} />

        <Divider sx={{ my: 1 }} component="li" />

        <MenuItemDelete
          staticLabel={t('domain:Workspace.Video.deleteVideoObject', { count: 1 })}
          onConfirm={handleDeleteVideoObject}
        />
      </ActionMenu>
      <Dialog fullWidth open={dialog} onClose={() => setDialog(false)}>
        {videoObject ? <LifecyclePolicyDialog objectData={videoObject} onSuccess={() => setDialog(false)} /> : null}
      </Dialog>
    </>
  )
}

export const ActionsMenu = memo(ActionsMenuRaw)
